import pick from 'lodash/pick';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { listingsAPI, usersAPI } from '../../api';
import { denormalisedResponseEntities, isUserInvitedToOrganization } from '../../util/data';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { showOrganizationProfile } from '../OrganizationProfilePage/OrganizationProfilePage.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { queryRecentlyViewedListings, saveRecentlyViewedListingId } from '../../ducks/user.duck';
import { fetchReviews } from '../ReviewsPage/ReviewsPage.duck';
import { listingDetailIncludeParams } from '../../util/imageVariants';
import { PAGE_TYPE_QUERY } from '../../util/listingType';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/ListingPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const QUERY_USERS_REQUEST = 'app/ListingPage/QUERY_USERS_REQUEST';
export const QUERY_USERS_SUCCESS = 'app/ListingPage/QUERY_USERS_SUCCESS';
export const QUERY_USERS_ERROR = 'app/ListingPage/QUERY_USERS_ERROR';

export const QUERY_SHOW_EXPERIENCES_REQUEST = 'app/ListingPage/QUERY_SHOW_EXPERIENCES_REQUEST';
export const QUERY_SHOW_EXPERIENCES_SUCCESS = 'app/ListingPage/QUERY_SHOW_EXPERIENCES_SUCCESS';
export const QUERY_SHOW_EXPERIENCES_ERROR = 'app/ListingPage/QUERY_SHOW_EXPERIENCES_ERROR';

export const QUERY_SIMILAR_SHOWS_REQUEST = 'app/ListingPage/QUERY_SIMILAR_SHOWS_REQUEST';
export const QUERY_SIMILAR_SHOWS_SUCCESS = 'app/ListingPage/QUERY_SIMILAR_SHOWS_SUCCESS';
export const QUERY_SIMILAR_SHOWS_ERROR = 'app/ListingPage/QUERY_SIMILAR_SHOWS_ERROR';

export const JOIN_WAITLIST_REQUEST = 'app/ListingPage/JOIN_WAITLIST_REQUEST';
export const JOIN_WAITLIST_SUCCESS = 'app/ListingPage/JOIN_WAITLIST_SUCCESS';
export const JOIN_WAITLIST_ERROR = 'app/ListingPage/JOIN_WAITLIST_ERROR';

export const LEAVE_WAITLIST_REQUEST = 'app/ListingPage/LEAVE_WAITLIST_REQUEST';
export const LEAVE_WAITLIST_SUCCESS = 'app/ListingPage/LEAVE_WAITLIST_SUCCESS';
export const LEAVE_WAITLIST_ERROR = 'app/ListingPage/LEAVE_WAITLIST_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingInProgress: false,
  showListingError: null,
  userIds: [],
  queryUsersInProgress: false,
  queryUsersError: null,
  hasShowExperience: false,
  showLiveRoomsIds: [],
  queryShowLiveRoomsInProgress: false,
  queryShowLiveRoomsError: null,
  similarShowIds: [],
  querySimilarShowsInProgress: false,
  querySimilarShowsError: null,
  joinWaitlistInProgress: false,
  joinWaitlistError: null,
  leaveWaitlistInProgress: false,
  leaveWaitlistError: null,
};

const resultIds = data => data.data.map(d => d.id);

export const getListing = (id, state) => {
  const ref = { id, type: 'listing' };
  const listings = getMarketplaceEntities(state, [ref]);
  return listings.length === 1 ? listings[0] : null;
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      // A refusal belongs to the listing it came from
      return {
        ...state,
        id: payload.id,
        showListingInProgress: true,
        showListingError: null,
        joinWaitlistError: null,
        leaveWaitlistError: null,
      };
    case SHOW_LISTING_SUCCESS:
      return { ...state, showListingInProgress: false, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingInProgress: false, showListingError: payload };

    case QUERY_USERS_REQUEST:
      return {
        ...state,
        userIds: [],
        queryUsersInProgress: true,
        queryUsersError: null,
      };
    case QUERY_USERS_SUCCESS:
      return {
        ...state,
        userIds: resultIds(payload).filter(i => payload.queryIds.includes(i.uuid)),
        queryUsersInProgress: false,
        queryUsersError: null,
      };
    case QUERY_USERS_ERROR:
      return {
        ...state,
        userIds: [],
        queryUsersInProgress: false,
        queryUsersError: payload,
      };

    case QUERY_SHOW_EXPERIENCES_REQUEST:
      return {
        ...state,
        queryShowLiveRoomsInProgress: true,
        queryShowLiveRoomsError: null,
      };
    case QUERY_SHOW_EXPERIENCES_SUCCESS:
      const liveRoomIds = resultIds(payload.data);
      const hasShowExperience = liveRoomIds.length > 0;
      return {
        ...state,
        showLiveRoomsIds: liveRoomIds,
        queryShowLiveRoomsInProgress: false,
        queryShowLiveRoomsError: null,
        hasShowExperience,
      };
    case QUERY_SHOW_EXPERIENCES_ERROR:
      return {
        ...state,
        showLiveRoomsIds: [],
        queryShowLiveRoomsInProgress: true,
        queryShowLiveRoomsError: null,
      };

    case QUERY_SIMILAR_SHOWS_REQUEST:
      return {
        ...state,
        similarShowIds: [],
        querySimilarShowsInProgress: true,
        querySimilarShowsError: null,
      };
    case QUERY_SIMILAR_SHOWS_SUCCESS:
      const similarShowIds = resultIds(payload.data);
      return {
        ...state,
        querySimilarShowsInProgress: false,
        querySimilarShowsError: null,
        similarShowIds,
      };
    case QUERY_SIMILAR_SHOWS_ERROR:
      return {
        ...state,
        similarShowIds: [],
        querySimilarShowsInProgress: true,
        querySimilarShowsError: null,
      };

    case JOIN_WAITLIST_REQUEST:
      return {
        ...state,
        joinWaitlistInProgress: true,
        joinWaitlistError: null,
      };
    case JOIN_WAITLIST_SUCCESS:
      return {
        ...state,
        joinWaitlistInProgress: false,
        joinWaitlistError: null,
      };
    case JOIN_WAITLIST_ERROR:
      return {
        ...state,
        joinWaitlistInProgress: false,
        joinWaitlistError: payload,
      };

    case LEAVE_WAITLIST_REQUEST:
      return {
        ...state,
        leaveWaitlistInProgress: true,
        leaveWaitlistError: null,
      };
    case LEAVE_WAITLIST_SUCCESS:
      return {
        ...state,
        leaveWaitlistInProgress: false,
        leaveWaitlistError: null,
      };
    case LEAVE_WAITLIST_ERROR:
      return {
        ...state,
        leaveWaitlistInProgress: false,
        leaveWaitlistError: payload,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});
export const showListingSuccess = () => ({ type: SHOW_LISTING_SUCCESS });
export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryUsersRequest = () => ({
  type: QUERY_USERS_REQUEST,
});
export const queryUsersSuccess = (response, queryIds) => ({
  type: QUERY_USERS_SUCCESS,
  payload: { data: response.data.data, queryIds },
});
export const queryUsersError = e => ({
  type: QUERY_USERS_ERROR,
  error: true,
  payload: e,
});

export const queryShowExperiencesRequest = () => ({
  type: QUERY_SHOW_EXPERIENCES_REQUEST,
});
export const queryShowExperiencesSuccess = response => ({
  type: QUERY_SHOW_EXPERIENCES_SUCCESS,
  payload: { data: response.data },
});
export const queryShowLiveRoomsError = e => ({
  type: QUERY_SHOW_EXPERIENCES_ERROR,
  error: true,
  payload: e,
});

export const querySimilarShowsRequest = () => ({
  type: QUERY_SIMILAR_SHOWS_REQUEST,
});
export const querySimilarShowsSuccess = response => ({
  type: QUERY_SIMILAR_SHOWS_SUCCESS,
  payload: { data: response.data },
});
export const querySimilarShowsError = e => ({
  type: QUERY_SIMILAR_SHOWS_ERROR,
  error: true,
  payload: e,
});

export const joinWaitlistRequest = () => ({
  type: JOIN_WAITLIST_REQUEST,
});
export const joinWaitlistSuccess = response => ({
  type: JOIN_WAITLIST_SUCCESS,
  payload: response,
});
export const joinWaitlistError = e => ({
  type: JOIN_WAITLIST_ERROR,
  error: true,
  payload: e,
});

export const leaveWaitlistRequest = () => ({
  type: LEAVE_WAITLIST_REQUEST,
});
export const leaveWaitlistSuccess = response => ({
  type: LEAVE_WAITLIST_SUCCESS,
  payload: response,
});
export const leaveWaitlistError = e => ({
  type: LEAVE_WAITLIST_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const fetchListingTeamIds = (listing, dispatch) => {
  const teamFromPublicData = listing.attributes.publicData.team || [];
  const teamIds = teamFromPublicData.filter(i => !i.pending).map(i => i.id);

  return dispatch(queryUsers(teamIds));
};

export const showListing = (listingId, isOwn = false, useIntegration = false) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(showListingRequest(listingId));

  const params = {
    id: listingId,
    ...listingDetailIncludeParams(),
  };

  const show = isOwn
    ? useIntegration
      ? listingsAPI.show({
          ...params,
          id: listingId.uuid,
        })
      : sdk.ownListings.show(params)
    : sdk.listings.show(params);

  return show
    .then(response => {
      const data = isOwn && useIntegration ? response.data : response;
      const listing = denormalisedResponseEntities(data)?.[0];

      const organizationProfileId = listing.attributes.publicData.organizationId;

      dispatch(showListingSuccess(data));
      dispatch(addMarketplaceEntities(data));
      if (organizationProfileId) {
        dispatch(showOrganizationProfile(organizationProfileId));
      }
      fetchListingTeamIds(listing, dispatch);

      return listing;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const queryUsers = queryIds => async (dispatch, getState, sdk) => {
  dispatch(queryUsersRequest());

  return usersAPI
    .byIds(queryIds || [])
    .then(response => {
      dispatch(addMarketplaceEntities(response.data));
      dispatch(queryUsersSuccess(response.data, queryIds));

      return response.data;
    })
    .catch(e => dispatch(queryUsersError(storableError(e))));
};

export const queryShowExperiences = (listingId, params) => (dispatch, getState, sdk) => {
  dispatch(queryShowExperiencesRequest());

  return sdk.listings
    .query({
      ...params,
      pub_type: config.listingTypes['live-room'],
      pub_showId: listingId.uuid,
      ...listingDetailIncludeParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(queryShowExperiencesSuccess(response));
      return response;
    })
    .catch(e => dispatch(queryShowLiveRoomsError(storableError(e))));
};

export const querySimilarShows = category => (dispatch, getState, sdk) => {
  dispatch(querySimilarShowsRequest());

  return sdk.listings
    .query({
      pub_category: category,
      pub_type: PAGE_TYPE_QUERY,
      ...listingDetailIncludeParams(),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(querySimilarShowsSuccess(response));
      return response;
    })
    .catch(e => dispatch(querySimilarShowsError(storableError(e))));
};

// loadData helper functions
const getCurrentListing = async listingId => {
  const currentListingResponse = await listingsAPI.show({ id: listingId });
  return currentListingResponse.data.data.data;
};

// A refusal is kept for the panel to show, and is no fault, so neither thunk rethrows. The server
// takes the person from the session and answers the list as it now is.
export const joinWaitlist = listingId => async dispatch => {
  dispatch(joinWaitlistRequest());

  try {
    const response = await listingsAPI.joinWaitlist(listingId.uuid);

    dispatch(addMarketplaceEntities(response));
    dispatch(joinWaitlistSuccess(response));

    return response;
  } catch (error) {
    dispatch(joinWaitlistError(storableError(error)));
    return null;
  }
};

export const leaveWaitlist = listingId => async dispatch => {
  dispatch(leaveWaitlistRequest());

  try {
    const response = await listingsAPI.leaveWaitlist(listingId.uuid);

    dispatch(addMarketplaceEntities(response));
    dispatch(leaveWaitlistSuccess(response));

    return response;
  } catch (error) {
    dispatch(leaveWaitlistError(storableError(error)));
    return null;
  }
};

export const loadData = params => async (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];

  if (ownListingVariants.includes(params.variant)) {
    const { currentUser } = getState().user;

    const currentListing = await getCurrentListing(listingId.uuid);
    const organizationProfiles = await dispatch(fetchOrganizationProfiles());
    const isCurrentUserInvitedToOrganization = isUserInvitedToOrganization(
      currentListing,
      organizationProfiles
    );
    const isCurrentUserInvitedToTeam = isUserInvitedToOrganization(currentUser, currentListing);
    const useIntegration = isCurrentUserInvitedToOrganization || isCurrentUserInvitedToTeam;

    const listingPromise = dispatch(showListing(listingId, true, useIntegration)).then(listing => {
      const category = listing?.attributes?.publicData?.category;
      dispatch(querySimilarShows(category));
      // The reviews are read from the listing in the store, so they wait for it
      return dispatch(fetchReviews(listingId, { onlyShownAuthors: true }));
    });
    const queryShowExperiencesPromise = dispatch(queryShowExperiences(listingId));

    return Promise.all([listingPromise, queryShowExperiencesPromise]);
  }

  // The server has no signed-in user and no localStorage: it loads the public part only.
  const isServer = typeof window === 'undefined';
  return Promise.all([
    dispatch(showListing(listingId)).then(listing => {
      const category = listing?.attributes?.publicData?.category;

      return Promise.all([
        dispatch(querySimilarShows(category)),
        dispatch(fetchReviews(listingId, { onlyShownAuthors: true })),
        isServer ? null : dispatch(loadClientData(params)),
      ]);
    }),
    dispatch(queryShowExperiences(listingId)),
  ]);
};

// The visitor's own part of the page, loaded in the browser (see onlyClientDataNeeded in
// src/Routes.js): their organizations and their recently viewed shows.
export const loadClientData = params => dispatch => {
  const listingId = new UUID(params.id);
  return Promise.all([
    dispatch(fetchOrganizationProfiles()),
    dispatch(saveRecentlyViewedListingId(listingId.uuid)).then(() =>
      dispatch(queryRecentlyViewedListings())
    ),
  ]);
};
